import { addNewPromptBlock } from 'actions/identifier';
import { fetchPromptTemplates, upsertAndAddPromptTemplate } from 'actions/promptTemplates';
import LoadingIndicator from 'components/LoadingIndicator';
import Modal from 'components/Modal';
import SearchInput from 'components/SearchInput';
import { MODAL_BACKGROUND_REDESIGN } from 'constants/common';
import { CLOSE_MODAL_ICON } from 'constants/commonIcons';
import { ARROW_CLOSE_ENVELOPE } from 'constants/envelopeIcons';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getPromptTemplates, getPromptTemplatesLoading } from 'selectors/promptTemplates';
import { useSelector } from 'store';
import { LLMPromptTemplate } from 'types';
import CreateOrEditBlockTemplate from './CreateOrEditBlockTemplate';

type TemplateComponentProps = {
  template: LLMPromptTemplate;
  expanded: boolean;
  setExpanded: (template: LLMPromptTemplate) => void;
  setEditPromptTemplate: (template: LLMPromptTemplate) => void;
  selectPromptTemplate: (template: LLMPromptTemplate) => void;
};

const TemplateItem: React.FC<TemplateComponentProps> = ({
  template,
  expanded,
  selectPromptTemplate,
  setExpanded,
  setEditPromptTemplate,
}) => (
  <div className="flex flex-col gap-2 border-b border-litlingo-gray-1">
    <button
      key={template.uuid}
      type="button"
      onClick={(e): void => {
        e.preventDefault();
        selectPromptTemplate(template);
      }}
      className="flex flex-row justify-between items-stretch w-full px-6 text-body text-litlingo-gray-3 focus:outline-none bg-white hover:bg-litlingo-gray-1"
    >
      <span className="py-2">{template.name}</span>

      <div className="flex flex-row justify-center items-center">
        <button
          type="button"
          onClick={(e): void => {
            e.stopPropagation();
            setExpanded(template);
          }}
          className={`flex flex-row justify-end w-24 focus:outline-none `}
        >
          <span
            className={`py-1.5  focus:outline-none transition-transform duration-300  ${
              expanded && 'transform rotate-90'
            }`}
          >
            {ARROW_CLOSE_ENVELOPE()}
          </span>
        </button>
      </div>
    </button>
    {expanded && (
      <div className="flex flex-col gap-1.5 px-6 pb-2">
        <p className="italic">Pattern: &quot;{template.pattern}&quot;</p>
        <button
          type="button"
          className="self-end text-litlingo-primary underline focus:outline-none"
          onClick={(): void => setEditPromptTemplate(template)}
        >
          Edit pattern
        </button>
      </div>
    )}
  </div>
);

type ComponentProps = {
  isModalOpen: boolean;
  toggleModalOpen: () => void;
};

const AddPromptBlockModal: React.FC<ComponentProps> = ({ isModalOpen, toggleModalOpen }) => {
  const dispatch = useDispatch();

  const loading = useSelector(getPromptTemplatesLoading);
  const templates = useSelector(getPromptTemplates);

  const [expanded, setExpanded] = useState<string>('');
  const [editTemplate, setEditTemplate] = useState<LLMPromptTemplate | null>(null);
  const [filtered, setFiltered] = useState<LLMPromptTemplate[]>([]);
  const [search, setSearch] = useState('');

  const [isCreatingNew, setIsCreatingNew] = useState(false);

  useEffect(() => {
    dispatch(fetchPromptTemplates());
  }, [dispatch]);

  const filterBySearch = useCallback((): void => {
    setFiltered(
      templates.filter((item) => {
        const { name } = item;
        if (name != null) return name.toLowerCase().includes(search.toLowerCase() || '');
        return '';
      })
    );
  }, [templates, search]);

  useEffect(() => {
    filterBySearch();
  }, [search, filterBySearch, templates]);

  const handleSearchChange = (value: string): void => {
    setSearch(value);
  };

  const handleSetExpanded = (template: LLMPromptTemplate): void => {
    if (expanded === template.uuid) {
      setExpanded('');
    } else {
      setExpanded(template.uuid);
    }
  };

  const toggleIsCreating = (): void => {
    setIsCreatingNew(!isCreatingNew);
    setEditTemplate(null);
  };

  const handleSetEditPromptTemplate = (template: LLMPromptTemplate): void => {
    toggleIsCreating();
    setEditTemplate(template);
  };

  const handleCreateAndAdd = (template: Partial<LLMPromptTemplate>): void => {
    dispatch(upsertAndAddPromptTemplate(template));
    toggleIsCreating();
    toggleModalOpen();
    setEditTemplate(null);
  };

  const handleSelectPromptTemplate = (template: LLMPromptTemplate): void => {
    dispatch(addNewPromptBlock({ template }));
    toggleModalOpen();
  };

  const templatesListComponent = (): JSX.Element => (
    <div className="flex flex-col w-full h-full overflow-auto">
      {templates.map((template) => (
        <TemplateItem
          key={template.uuid}
          template={template}
          expanded={expanded === template.uuid}
          setExpanded={handleSetExpanded}
          setEditPromptTemplate={handleSetEditPromptTemplate}
          selectPromptTemplate={handleSelectPromptTemplate}
        />
      ))}
    </div>
  );

  const templatesEmptyListComponent = (): JSX.Element => (
    <div className="h-full flex flex-col items-center text-center gap-2 pt-8">
      <h2 className="text-heading-3">Template not found</h2>
      <span>
        Suggestions: Make sure all words are spelled correctly or try a more general search.
      </span>
      <div>
        <button type="button" className="button button--primary h-8" onClick={toggleIsCreating}>
          Create New Block
        </button>
      </div>
    </div>
  );

  const body = (
    <>
      <div className="absolute top-0 left-0 right-0 h-22 z-0">{MODAL_BACKGROUND_REDESIGN}</div>
      <div className="flex flex-row justify-end pr-6 pt-6">
        <button
          type="button"
          data-testid="close-button"
          className="w-6 z-10 focus:outline-none "
          aria-label="Close"
          onClick={toggleModalOpen}
        >
          {CLOSE_MODAL_ICON}
        </button>
      </div>

      <div className="h-62 flex flex-col mt-14 mb-4 overflow-hidden flex-1">
        {loading && templates.length === 0 ? (
          <div className="w-full flex flex-row justify-center my-14 px-6">
            <LoadingIndicator size="20" />
          </div>
        ) : (
          <>
            <div className="px-6">
              <h2 className="text-heading-2">Select Prompt Block</h2>
              <div className="pt-4">
                <SearchInput
                  placeholder="Search workspace"
                  className="w-full h-8"
                  handleSearch={handleSearchChange}
                />
              </div>
            </div>

            <div className="border-b border-litlingo-gray-2 mt-2" />
            {filtered.length ? templatesListComponent() : templatesEmptyListComponent()}
            <div className="border-b border-litlingo-gray-2" />
          </>
        )}
      </div>
    </>
  );

  const noTemplatesBody = (
    <>
      <div className="absolute top-0 left-0 right-0 h-22 z-0">{MODAL_BACKGROUND_REDESIGN}</div>
      <div className="flex flex-row justify-end pr-6 pt-6">
        <button
          type="button"
          data-testid="close-button"
          className="w-6 z-10 focus:outline-none "
          aria-label="Close"
          onClick={toggleModalOpen}
        >
          {CLOSE_MODAL_ICON}
        </button>
      </div>
      <div className="flex flex-col items-center px-6 mt-30 mb-30 gap-4 max-h-128 overflow-hidden">
        <h2 className="text-heading-1">No block templates found</h2>
        <button type="button" className="button button--primary h-8" onClick={toggleIsCreating}>
          Create New Block
        </button>
      </div>
    </>
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          title=" "
          body={templates.length === 0 && !loading ? noTemplatesBody : body}
          toggleShowModal={toggleModalOpen}
          okButton={false}
          cancelButton
          cancelButtonOnclick={toggleIsCreating}
          cancelButtonText="New Block"
          cancelButtonStyle="mr-6"
          xButton={false}
          style={{
            width: '546px',
            minHeight: '415px',
            maxHeight: '415px',
            padding: '0px',
          }}
        />
      )}
      {isCreatingNew && (
        <CreateOrEditBlockTemplate
          isModalOpen={isCreatingNew}
          toggleModalOpen={toggleIsCreating}
          template={editTemplate}
          handleCreateBlock={handleCreateAndAdd}
        />
      )}
    </>
  );
};

export default AddPromptBlockModal;
